import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Modal,
  Typography,
  Select,
  InputLabel,
  FormControl,
  TextField,
  CircularProgress,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import useThemeColors from "../ThemeColors";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../../components/reusableComponent/CapitalLetter";
import { formatDate } from "../../../components/reusableComponent/DateFormat";
import ReactModal from "react-modal";
import { GridCloseIcon } from "@mui/x-data-grid";
import {
  useServiceRequestQuery,
  useGetWorkshopPendingAppointmentQuery,
} from "../../../Services/inventory/Inventory";
import { WORKSHOP_ID } from "../../../constants";
import { useNavigate } from "react-router-dom";
import CancelAppointment from "./CancelAppointment";
import RescheduleAppointment from "./RescheduleAppointment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import debounce from "lodash/debounce";
import { format, parseISO, isValid } from "date-fns";
import { useGetOnboardWorkshopDataQuery } from "../../../Services/vehicle/Vehicle";
import { toast } from "react-toastify";

interface Workshop {
  id: string;
  name: string;
  workshopName?: string;
}

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface Appointment {
  id: string;
  appointment_id?: string;
  workshop: {
    id: string;
    name: string;
  };
  serviceName: string;
  subServices:
    | Array<{
        id: string;
        name: string;
      }>
    | string[];
  appointmentDate: string;
  vehicleNumber: string;
  status: "pending" | "confirmed" | "completed" | "cancelled" | string;
  customer: {
    name: string;
    phone?: string;
  };
}

const Appointment = () => {
  const [appointmentsData, setAppointmentsData] = useState<Appointment[]>([]);
  const [tableLoader, setTableLoader] = useState<boolean>(false);
  const [openRescheduleModal, setOpenRescheduleModal] =
    useState<boolean>(false);
  const [openDetailsPage, setOpenDetailsPage] = useState<boolean>(false);
  const [getCompleteRowDetails, setGetCompleteRowDetails] =
    useState<Appointment | null>(null);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [storedAppointmentData, setStoredAppointmentData] = useState<
    Partial<Appointment>
  >({});

  const statusOptions = [
    "scheduled",
    "rescheduled",
    "missed",
    "cancelled",
    "completed",
  ] as const;
  type StatusType = (typeof statusOptions)[number];
  const [selectedStatuses, setSelectedStatuses] = useState<StatusType[]>([]);

  const handleStatusChange = (
    event: SelectChangeEvent<typeof selectedStatuses>
  ) => {
    const { value } = event.target;

    if (value.includes("all" as any)) {
      setSelectedStatuses(
        selectedStatuses.length === statusOptions.length
          ? []
          : [...statusOptions]
      );
      return;
    }

    setSelectedStatuses(
      typeof value === "string" ? (value.split(",") as StatusType[]) : value
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkshop, setSelectedWorkshop] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  const handleFilter = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);

  const ITEMS_PER_PAGE = 20;
  const navigate = useNavigate();
  const getWorkshopId = localStorage.getItem(WORKSHOP_ID);

  const {
    data: workshopData,
    isLoading: isLoadingWorkshops,
    status: workshopStatus,
    refetch: refetchWorkshops,
  } = useGetOnboardWorkshopDataQuery(1);

  const workshops = useMemo<Workshop[]>(() => {
    if (!workshopData || !Array.isArray(workshopData)) return [];
    return workshopData.map((workshop) => ({
      id: workshop.id,
      name: workshop.name || workshop.workshopName || "Unknown Workshop",
      workshopName:
        workshop.name || workshop.workshopName || "Unknown Workshop",
    }));
  }, [workshopData]);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("page_size", ITEMS_PER_PAGE.toString());

    if (selectedStatuses.length > 0) {
      params.append("status", selectedStatuses.join(","));
    }
    if (selectedWorkshop) {
      params.append("workshop_id", selectedWorkshop.toString());
    }
    if (dateRange.startDate) {
      params.append("from_date", dateRange.startDate);
    }
    if (dateRange.endDate) {
      params.append("to_date", dateRange.endDate);
    }

    return Object.fromEntries(params);
  }, [currentPage, selectedStatuses, selectedWorkshop, dateRange]);

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    isFetching: isFetchingAppointments,
    refetch,
  } = useGetWorkshopPendingAppointmentQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (appointmentData?.data) {
      setAppointmentsData(appointmentData.data);
    }
  }, [appointmentData]);

  const isLoading =
    isLoadingAppointments || isFetchingAppointments || tableLoader;

  const closeModal = () => {
    setOpenRescheduleModal(false);
    setOpenCancelModal(false);
  };

  useEffect(() => {
    if (openDetailsPage) {
      navigate(
        `/vehicle/appointment/${getCompleteRowDetails?.appointment_id}`,
        { state: getCompleteRowDetails }
      );
    }
  }, [getCompleteRowDetails, navigate, openDetailsPage]);

  const handleViewDetails = (appointment: Appointment) => {
    setGetCompleteRowDetails(appointment);
    setOpenDetailsPage(true);

    if (appointment) {
      navigate(
        `/vehicle/appointment/${appointment.id || appointment.appointment_id}`,
        { state: appointment }
      );
    }
  };

  const getWorkshopName = (workshopId: string) => {
    const workshop = workshops.find((w) => w.id === workshopId);
    return workshop
      ? workshop.name || workshop.workshopName || "Unknown Workshop"
      : "Unknown Workshop";
  };

  const formatSubServices = (
    subServices: Array<{ name: string }> | string[]
  ) => {
    if (!subServices || subServices.length === 0) return "N/A";
    if (typeof subServices[0] === "string") {
      return (subServices as string[]).join(", ");
    }
    return (subServices as Array<{ name: string }>)
      .map((s) => s.name)
      .join(", ");
  };

  const rescheduleAppt = (appt) => {
    setStoredAppointmentData(appt);
    setOpenRescheduleModal(true);
  };

  const deleteService = (appointment) => {
    setStoredAppointmentData(appointment);
    setOpenCancelModal(true);
  };

  const formatDate1 = (dateStr: string | undefined): string => {
    if (!dateStr) return "N/A";

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Invalid date";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours || 12;

      return `${day}/${month}/${year}, ${hours}:${minutes} ${period}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // CSV Download Function with API Call
  const handleDownloadCSV = async () => {
    if (!appointmentsData || appointmentsData.length === 0) {
      toast.warning("No data available to download");
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.loading("Preparing CSV export...");

      // Build query parameters with filters (only add if they have values)
      const queryParams = new URLSearchParams();

      // Only add status if at least one status is selected
      if (selectedStatuses.length > 0) {
        queryParams.append("status", selectedStatuses.join(","));
      }

      // Only add workshop_id if a workshop is selected
      if (selectedWorkshop) {
        queryParams.append("workshop_id", selectedWorkshop.toString());
      }

      // Only add dates if they are selected
      if (dateRange.startDate) {
        queryParams.append("from_date", dateRange.startDate);
      }
      if (dateRange.endDate) {
        queryParams.append("to_date", dateRange.endDate);
      }

      // Get API base URL dynamically
      const fetchingTenant = window.location.hostname.split(".")[0];
      const apiPort = process.env.REACT_APP_API_PROTOCOL;
      const API_BASE_URL = `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc`;

      // Build the URL - if no filters, query string will be empty
      const queryString = queryParams.toString();
      const apiUrl = `${API_BASE_URL}/workshop-booking-requests/export-booking-requests/${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Check if response is not ok
      if (!response.ok) {
        // Try to parse error message from response
        const contentType = response.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          const errorMessage =
            errorData.message || errorData.error || "Failed to export CSV";
          toast.error(errorMessage);
        } else {
          toast.error(`Failed to export CSV: ${response.statusText}`);
        }
        return;
      }

      // Check the content type to determine file format
      const contentType = response.headers.get("content-type");

      // If response is JSON, it might be an error message even with 200 status
      if (contentType && contentType.includes("application/json")) {
        const jsonData = await response.json();

        // Check if it's an error response
        if (jsonData.message && !jsonData.data) {
          toast.error(jsonData.message);
          return;
        }
      }

      const contentDisposition = response.headers.get("content-disposition");

      // Get the blob
      const blob = await response.blob();

      // Validate blob
      if (blob.size === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Determine file extension based on content type
      let fileExtension = "csv";
      let fileName = `pending_appointments_${
        new Date().toISOString().split("T")[0]
      }`;

      if (
        contentType?.includes("spreadsheet") ||
        contentType?.includes("excel")
      ) {
        fileExtension = "xlsx";
      } else if (contentDisposition) {
        // Try to extract filename from content-disposition header
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition
        );
        if (matches && matches[1]) {
          const suggestedFileName = matches[1].replace(/['"]/g, "");
          fileName = suggestedFileName.split(".")[0];
          fileExtension = suggestedFileName.split(".").pop() || "csv";
        }
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.${fileExtension}`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      toast.success(`File exported successfully!`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export file. Please try again.");
    }
  };

  const totalItems = appointmentData?.pagination?.total_count || 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatuses, selectedWorkshop, dateRange]);

  const handleResetFilters = () => {
    setSelectedStatuses([]);
    setSelectedWorkshop("");
    setDateRange({ startDate: null, endDate: null });
    setCurrentPage(1);
  };

  const selectedRowClicked = (service) => {
    setOpenDetailsPage(true);
    setGetCompleteRowDetails(service);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="vehicle-info-cont">
        <div className="workshops-container">
          <div className="workshops-header">
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <span
                  style={{
                    fontWeight: "200",
                    fontSize: "20px",
                    color: "#666666",
                    fontFamily: "karbon",
                  }}
                >
                  Pending Appointments
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  marginBottom: "15px",
                }}
              >
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel sx={{ fontFamily: "Karbon-medium" }}>
                    Status
                  </InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    onChange={handleStatusChange}
                    label="Status"
                    sx={{
                      fontFamily: "Karbon-medium",
                      "& .MuiSelect-select": {
                        fontFamily: "Karbon-medium",
                      },
                    }}
                    renderValue={(selected) => {
                      if (selected.length === 0)
                        return (
                          <span style={{ fontFamily: "Karbon-medium" }}>
                            All Statuses
                          </span>
                        );
                      if (selected.length === statusOptions.length)
                        return (
                          <span style={{ fontFamily: "Karbon-medium" }}>
                            All Statuses
                          </span>
                        );
                      return selected.length > 1 ? (
                        <span
                          style={{ fontFamily: "Karbon-medium" }}
                        >{`${selected.length} statuses selected`}</span>
                      ) : (
                        <span style={{ fontFamily: "Karbon-medium" }}>
                          {selected[0].charAt(0).toUpperCase() +
                            selected[0].slice(1)}
                        </span>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          fontFamily: "Karbon-medium",
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value="all"
                      key="all"
                      sx={{ fontFamily: "Karbon-medium" }}
                    >
                      <Checkbox
                        checked={
                          selectedStatuses.length === statusOptions.length
                        }
                        indeterminate={
                          selectedStatuses.length > 0 &&
                          selectedStatuses.length < statusOptions.length
                        }
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                      />
                      <span style={{ marginLeft: "8px" }}>All Statuses</span>
                    </MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem
                        key={status}
                        value={status}
                        sx={{ fontFamily: "Karbon-medium" }}
                      >
                        <Checkbox
                          checked={selectedStatuses.includes(status)}
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                        />
                        <span style={{ marginLeft: "8px" }}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  sx={{
                    minWidth: 200,
                    maxWidth: 200,
                    "& .MuiInputBase-root": { fontFamily: "Karbon-medium" },
                  }}
                  size="small"
                >
                  <InputLabel sx={{ fontFamily: "Karbon-medium" }}>
                    Workshop
                  </InputLabel>
                  <Select
                    value={selectedWorkshop}
                    onChange={(e) =>
                      setSelectedWorkshop(e.target.value as string)
                    }
                    label="Workshop"
                    sx={{
                      "& .MuiSelect-select": {
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        maxWidth: "180px",
                        padding: "8px 32px 8px 12px",
                        fontSize: "0.875rem",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 200,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: "Karbon-medium" }}>
                      All Workshops
                    </MenuItem>
                    {workshops.map((workshop) => (
                      <MenuItem
                        key={workshop.id}
                        value={workshop.id}
                        sx={{ fontFamily: "Karbon-medium" }}
                      >
                        {workshop.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From Date"
                    value={
                      dateRange.startDate ? new Date(dateRange.startDate) : null
                    }
                    onChange={(date: Date | null) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : null;
                      setDateRange((prev) => ({
                        ...prev,
                        startDate: formattedDate,
                      }));
                    }}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          "& .MuiInputBase-root": {
                            fontFamily: "Karbon-medium",
                          },
                          "& .MuiInputLabel-root": {
                            fontFamily: "Karbon-medium",
                          },
                        },
                        inputProps: { style: { fontFamily: "Karbon-medium" } },
                      },
                    }}
                  />

                  <DatePicker
                    label="To Date"
                    value={
                      dateRange.endDate ? new Date(dateRange.endDate) : null
                    }
                    onChange={(date: Date | null) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : null;
                      setDateRange((prev) => ({
                        ...prev,
                        endDate: formattedDate,
                      }));
                    }}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          "& .MuiInputBase-root": {
                            fontFamily: "Karbon-medium",
                          },
                          "& .MuiInputLabel-root": {
                            fontFamily: "Karbon-medium",
                          },
                        },
                        inputProps: { style: { fontFamily: "Karbon-medium" } },
                      },
                    }}
                  />
                </LocalizationProvider>

                <Button
                  variant="contained"
                  onClick={handleResetFilters}
                  sx={{
                    fontFamily: "Karbon-medium",
                    fontSize: "14px",
                    textTransform: "none",
                    backgroundColor: "#FF6B35",
                    borderRadius: "4px",
                    padding: "8px 20px",
                    height: "40px",
                    "&:hover": {
                      backgroundColor: "#E65A2B",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    },
                  }}
                  disabled={isLoading || tableLoader}
                >
                  {isLoading || tableLoader ? (
                    <CircularProgress
                      size={20}
                      sx={{ color: "inherit", mr: 1 }}
                    />
                  ) : null}
                  Reset Filters
                </Button>

                <Button
                  variant="contained"
                  onClick={handleDownloadCSV}
                  sx={{
                    fontFamily: "Karbon-medium",
                    fontSize: "14px",
                    textTransform: "none",
                    backgroundColor: "#FF6B35",
                    borderRadius: "4px",
                    padding: "8px 20px",
                    height: "40px",
                    "&:hover": {
                      backgroundColor: "#E65A2B",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    },
                  }}
                  disabled={!appointmentsData || appointmentsData.length === 0}
                >
                  <i
                    className="bi bi-download text-white"
                    style={{ marginRight: "8px" }}
                  ></i>
                  Download CSV
                </Button>
              </div>
            </div>
          </div>

          {isLoading || tableLoader ? (
            <LoadingSpinner />
          ) : (
            <div className="workshops-table-container">
              <table className="workshops-table">
                <thead>
                  <tr style={{ background: "#F7D3C5" }}>
                    <th className="table-header">Request Id</th>
                    <th className="table-header">Customer Name</th>
                    <th className="table-header">Phone Number</th>
                    <th className="table-header">Service</th>
                    <th className="table-header">Sub Service</th>
                    <th className="table-header">Created At</th>
                    <th className="table-header">Appointment Date</th>
                    <th className="table-header">Workshop</th>
                    <th className="table-header">Workshop Email</th>
                    <th className="table-header">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appointmentsData?.map((workshop: any, index: number) => (
                    <tr
                      key={workshop.id}
                      className={
                        index % 2 === 1 ? "row-alternate" : "row-normal"
                      }
                    >
                      <td className="table-cell">{workshop?.id}</td>
                      <td className="table-cell">
                        {capitalizeFirstLetter(
                          workshop?.customer_details?.first_name
                        )}
                      </td>
                      <td className="table-cell">
                        {capitalizeFirstLetter(
                          workshop?.customer_details?.phone
                        )}
                      </td>
                      <td className="table-cell">
                        {workshop?.service_details?.map(
                          (service: any, index: number) => (
                            <div key={index}>
                              {index + 1}. {service?.name}
                            </div>
                          )
                        )}
                      </td>
                      <td className="table-cell">
                        {workshop?.service_details?.flatMap((service: any) =>
                          service?.sub_services?.map(
                            (sub: any, index: number) => (
                              <div key={`${service.id}-${index}`}>
                                {index + 1}. {sub?.name}
                              </div>
                            )
                          )
                        )}
                      </td>
                      <td
                        className="table-cell"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "200px",
                        }}
                      >
                        {formatDate(workshop?.created_at)}
                      </td>
                      <td
                        className="table-cell"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "200px",
                        }}
                      >
                        {formatDate(workshop?.service_date)}
                      </td>
                      <td className="table-cell">
                        {workshop?.workshop_details?.workshopName}
                      </td>
                      <td className="table-cell">
                        {
                          workshop?.workshop_details
                            ?.workShopAdministrationEmail
                        }
                      </td>
                      <td className="table-cell">
                        {capitalizeFirstLetter(workshop?.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="pagination-footer">
            <div className="pagination-info">
              Showing {displayStart} - {endIndex} of {totalItems}
            </div>

            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Prev
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let firstPage = Math.max(
                  1,
                  Math.min(currentPage - 2, totalPages - 4)
                );
                const pageNum = firstPage + i;

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-btn ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                    style={{
                      display: pageNum > totalPages ? "none" : "inline-block",
                      margin: "0 2px",
                      minWidth: "32px",
                      textAlign: "center",
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default Appointment;
